import React, { Suspense, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentContext } from "go-segment/context";
import { ProductType } from "../../../../../../../../services/Api/Organization/types";
import { ProductsMenuContext } from "../../index";
import { ProductList } from "./ProductList";
import { productListName, productResourceType } from "./utils";

interface Props {
	productType: ProductType;
}

const ProductListPage = ({ productType }: Props) => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const { setMenuState } = useContext(ProductsMenuContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	const customFieldResourceTypes = [
		{
			type: "PRODUCT",
			name: t(`enums.custom_fields.resources.PRODUCT`),
		},
	];

	useEffect(() => {
		handleChangeTabTitle(t("modules.product.header.title"));
		setResource(
			wrapPromise(segmentContextValue.get(productListName, productResourceType, customFieldResourceTypes))
		);
	}, []);

	useEffect(() => {
		setMenuState(productType === "COMPOSITE" ? "composites" : "products");
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/products${productType === "COMPOSITE" ? "/composite" : ""}/new`,
			variant: "primary",
			state: { type: productType },
		},
		{
			title: t("common.action.import_from_csv", { ns: "lib" }),
			path: `/${organization.id}/products/import`,
			dropdown: true,
			hide: productType === "COMPOSITE",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/products${productType === "COMPOSITE" ? "/composite" : ""}/new`,
			state: { type: productType },
		},
		{
			title: t("common.action.import_from_csv", { ns: "lib" }),
			path: `/${organization.id}/products/import`,
			hide: productType === "COMPOSITE",
		},
	];

	return (
		<>
			{!isMobile && (
				<Header
					title={
						productType === "COMPOSITE"
							? t("modules.product_composite.header.title")
							: t("modules.product.header.title")
					}
					buttons={buttons}
				/>
			)}
			<Suspense fallback={<LoadingContainer />}>
				<ProductList resource={resource} mobileActions={mobileActions} productType={productType} />
			</Suspense>
		</>
	);
};

export default ProductListPage;
